/* ============================================================
 * TUNExperience · Hub — sesión, navegación y carga de datos
 *
 * El artifact original guardaba todo en un objeto global que los
 * componentes mutaban, y forzaba el repintado con un `force()`. Funciona
 * mientras los datos están en memoria y las lecturas son instantáneas.
 * Contra un backend deja de valer: hay que esperar, puede fallar, y dos
 * pantallas pueden estar viendo cosas distintas.
 *
 * Aquí se sustituye por lo mínimo que hace falta: un contexto para sesión
 * y navegación, y un hook de carga que resuelve los tres estados que toda
 * lectura por red tiene (cargando, error, datos).
 * ============================================================ */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api, sesionGuardada, guardarSesion, borrarSesion } from './api.js';
import { isoOffset } from './utiles/fechas.js';

const Ctx = createContext(null);
export const useCtx = () => useContext(Ctx);

export function Proveedor({ children }) {
  // La sesión se recupera de localStorage para que un refresco no eche a
  // nadie del hub a mitad de una jornada.
  const [session, setSession] = useState(sesionGuardada);
  const [view, setView] = useState('global');
  const [sedeId, setSedeId] = useState(null);
  const [boardDate, setBoardDate] = useState(isoOffset(0));
  const [modal, setModal] = useState(null);

  // Cambiar este número hace que los recursos vuelvan a pedirse. Es la
  // sustitución del force(): en vez de mutar datos y repintar, se declara
  // que lo que había en pantalla ya no vale y se pide de nuevo al servidor,
  // que es quien manda.
  const [revision, setRevision] = useState(0);
  const refrescar = useCallback(() => setRevision((n) => n + 1), []);

  const entrar = useCallback((token, datos) => {
    guardarSesion(token, datos);
    setSession(datos);
    setView(datos.rol === 'aleph' ? 'global' : 'sede');
    setSedeId(datos.rol === 'aleph' ? null : datos.sedeId);
  }, []);

  const logout = useCallback(() => {
    borrarSesion();
    // Sin esto, Google vuelve a identificar sola a la misma persona en cuanto
    // se recarga la página: cerrar sesión no serviría de nada en un ordenador
    // compartido, que es exactamente el del mostrador de un centro.
    window.google?.accounts?.id?.disableAutoSelect?.();
    setSession(null);
    setView('global');
    setSedeId(null);
  }, []);

  const go = useCallback((v, sid = null) => {
    setView(v);
    setSedeId(sid);
    setModal(null);
    window.scrollTo(0, 0);
  }, []);

  const valor = {
    session, entrar, logout,
    view, sedeId, go,
    boardDate, setBoardDate,
    modal, openModal: setModal, closeModal: () => setModal(null),
    revision, refrescar,
  };

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

/**
 * Carga un recurso del backend y devuelve sus tres estados posibles.
 *
 * `cargar` debe ser estable o depender solo de `deps`, como en useEffect.
 * Se reintenta solo cuando cambian las dependencias o cuando alguien llama
 * a refrescar() tras una escritura.
 */
export function useRecurso(cargar, deps = []) {
  const { revision } = useCtx();
  const [estado, setEstado] = useState({ datos: null, cargando: true, error: null });
  // Evita que una respuesta lenta de una petición ya obsoleta pise a otra
  // más reciente: al cambiar de sede rápido, la primera puede llegar la última.
  const vigente = useRef(0);

  useEffect(() => {
    const turno = ++vigente.current;
    setEstado((e) => ({ ...e, cargando: true, error: null }));
    cargar()
      .then((datos) => { if (turno === vigente.current) setEstado({ datos, cargando: false, error: null }); })
      .catch((error) => { if (turno === vigente.current) setEstado({ datos: null, cargando: false, error }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, revision]);

  return estado;
}

/** Envuelve una acción de escritura: bloquea, captura el error y refresca. */
export function useAccion() {
  const { refrescar } = useCtx();
  const [enCurso, setEnCurso] = useState(false);
  const [error, setError] = useState('');
  const [errorDatos, setErrorDatos] = useState(null);

  const ejecutar = useCallback(async (fn, alTerminar) => {
    setEnCurso(true);
    setError('');
    setErrorDatos(null);
    try {
      const salida = await fn();
      refrescar();                 // el servidor manda: se releen los datos
      if (alTerminar) alTerminar(salida);
      return salida;
    } catch (e) {
      setError(e.message);
      setErrorDatos(e.datos || null);
      return null;
    } finally {
      setEnCurso(false);
    }
  }, [refrescar]);

  return { ejecutar, enCurso, error, errorDatos, setError };
}

export { api };
